/**
 * ezyVet telemetry, backfill and webhook-log endpoints.
 *
 * Connection setup (credentials, test, webhooks) lives in ./integrations.ts
 * against /api/v1/integrations/ezyvet. The endpoints here follow the
 * integration spec's contract; pages using them show a "not available" state
 * when the backend answers 404, so the paths can land independently.
 */
import { apiClient } from "./client";
import { testEzyVet } from "./integrations";
import type { EzyVetStatus } from "@/types";
import type {
  BackfillRequest,
  BackfillResponse,
  EzyVetTelemetry,
  Page,
  WebhookEventStatus,
  WebhookLogEntry,
} from "@/types/ezyvet";

const BASE = "/api/v1/ezyvet";

export async function getEzyVetTelemetry(): Promise<EzyVetTelemetry> {
  const { data } = await apiClient.get<EzyVetTelemetry>(`${BASE}/status`);
  return data;
}

export interface Diagnostics {
  status: EzyVetStatus;
  latency_ms: number;
}

/** Forces a fresh token with the stored credentials and times the round trip. */
export async function diagnoseEzyVet(): Promise<Diagnostics> {
  const started = performance.now();
  const status = await testEzyVet();
  return { status, latency_ms: Math.round(performance.now() - started) };
}

/** Queues a re-read of every consult between the two dates (inclusive). */
export async function startBackfill(req: BackfillRequest): Promise<BackfillResponse> {
  const { data } = await apiClient.post<BackfillResponse>(`${BASE}/backfill`, req);
  return data;
}

export interface WebhookLogQuery {
  limit: number;
  offset: number;
  search?: string;
  status?: WebhookEventStatus;
}

export async function listWebhookLogs(q: WebhookLogQuery): Promise<Page<WebhookLogEntry>> {
  const { data } = await apiClient.get<Page<WebhookLogEntry>>(`${BASE}/webhooks/logs`, {
    params: {
      limit: q.limit,
      offset: q.offset,
      ...(q.search ? { search: q.search } : {}),
      ...(q.status ? { status: q.status } : {}),
    },
  });
  return data;
}
